/**
 * Islamic Guidance Messages for Rate Limiting
 * Provides context-appropriate, respectful messages aligned with Islamic values
 * Following soft, modern tone with patience and charity themes
 */

/**
 * Get Islamic guidance message for rate limit scenarios
 * @param scenario - The rate limit scenario
 * @param remaining - Optional remaining messages count
 * @param retryAfter - Optional seconds until retry
 * @returns Context-appropriate Islamic guidance message
 */
export function getIslamicGuidance(
  scenario: 'rate_limit_reached' | 'approaching_limit' | 'recovery' | 'burst_warning',
  remaining?: number,
  retryAfter?: number
): string {
  switch (scenario) {
    case 'rate_limit_reached':
      if (retryAfter) {
        const minutes = Math.floor(retryAfter / 60);
        const seconds = retryAfter % 60;
        const timeText = minutes > 0 
          ? `${minutes} minute${minutes > 1 ? 's' : ''} and ${seconds} second${seconds !== 1 ? 's' : ''}`
          : `${seconds} second${seconds !== 1 ? 's' : ''}`;
        
        return `Patience is a virtue in charity. Your limit will reset automatically in ${timeText}.`;
      }
      return 'Patience is a virtue in charity. Your limit will reset automatically.';

    case 'approaching_limit':
      if (remaining !== undefined) {
        if (remaining === 1) {
          return 'This is your last message. Please be thoughtful in your giving.';
        }
        if (remaining <= 3) {
          return `You have ${remaining} messages remaining. Please slow down. Thoughtful giving is valued.`;
        }
        return `You have ${remaining} messages remaining. Please consider spacing out your messages.`;
      }
      return 'Please slow down. Thoughtful giving is valued.';

    case 'recovery':
      return 'Your limit has reset. You may continue your charitable conversation.';

    case 'burst_warning':
      return 'You are sending messages too quickly. Please take a moment to reflect.';

    default:
      return 'Please be patient. Your charitable conversation will continue shortly.';
  }
}

/**
 * Get title for rate limit warnings
 * @param scenario - The rate limit scenario
 * @returns Appropriate title
 */
export function getRateLimitTitle(scenario: 'rate_limit_reached' | 'approaching_limit' | 'recovery' | 'burst_warning'): string {
  switch (scenario) {
    case 'rate_limit_reached':
      return 'Rate Limit Reached';
    case 'approaching_limit':
      return 'Approaching Limit';
    case 'recovery':
      return 'Limit Reset';
    case 'burst_warning':
      return 'Sending Too Quickly';
    default:
      return 'Rate Limit Notice';
  }
}

